import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { apiClient } from '@api/client';
import { useAuthStore } from '@store/authStore';
import type { GoogleChatConnection } from '@store/authStore';
import { useNotificationStore } from '@store/notificationStore';

interface GoogleChatAuthorizationUrlResponse {
  authorization_url: string;
}

const extractErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    return error.response?.data?.detail || 'Google Chat request failed';
  }
  return 'Google Chat request failed';
};

export const useConnectGoogleChat = () => {
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({ returnPath, mode = 'admin' }: { returnPath: string; mode?: 'admin' | 'user' }) => {
      const { data } = await apiClient.get<GoogleChatAuthorizationUrlResponse>(
        `/auth/google-chat/url?return_path=${encodeURIComponent(returnPath)}&mode=${mode}`
      );
      return data;
    },
    onError: (error) => {
      addNotification({
        message: extractErrorMessage(error),
        type: 'error',
      });
    },
  });
};

export const useDisconnectGoogleChat = () => {
  const queryClient = useQueryClient();
  const { currentUser, setCurrentUser } = useAuthStore();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<GoogleChatConnection>('/auth/google-chat/disconnect');
      return data;
    },
    onSuccess: (googleChatConnection) => {
      if (currentUser) {
        setCurrentUser({ ...currentUser, google_chat_connection: googleChatConnection });
      }
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      addNotification({
        message: 'Google Chat disconnected',
        type: 'success',
      });
    },
    onError: (error) => {
      addNotification({
        message: extractErrorMessage(error),
        type: 'error',
      });
    },
  });
};
